'use client'
import { memo, useEffect, useMemo, useState, type FC } from 'react'
import styled from 'styled-components'
import { type TypeDevice, useDeviceType, useTranslator } from '@/hooks'
import { designGrid, mq, presets, rgbAlpha } from '@/styles'
import { SliderFlickity, Video } from './base'

type Props = {
  deviceType?: TypeDevice
}

type TypeSectionSteps = {
  title: string
  description: string
  steps: {
    title: string
    description: string
  }[]
}

const isVideo = {
  'step_01': true,
  'step_02': true,
  'step_03': true,
  'step_04': true,
  'step_05': true,
  'step_07': true
}

const getItems = (data: TypeSectionSteps['steps']) => data.map((item, idx) => {
  const position = idx + 1
  const step = `step_${position > 9 ? position : `0${position}`}`
  const isVideoStep = isVideo[step]
  const href = isVideoStep ? `/videos/steps/${step}` : `/images/steps/${step}.jpg`

  return (
    <li key={idx}>
      <article>
        <figure className='media-wrapper'>
          {isVideoStep ?
            <Video className='media' src={[
              { src: href + '.webm', type: 'video/webm' },
              { src: href + '.mp4', type: 'video/mp4' }]}
            />
            :
            // eslint-disable-next-line @next/next/no-img-element
            <img className='media' src={href} alt={`Step ${position}`} />
          }
        </figure>
        <hgroup>
          <h3>
            <span>{idx < 10 ? 0 : ''}{idx + 1}</span>
            <span dangerouslySetInnerHTML={{ __html: item.title }} />
          </h3>
          <div dangerouslySetInnerHTML={{ __html: item.description }}></div>
        </hgroup>
      </article>
    </li>
  )
})

export const SectionSteps:FC<Props> = memo(({ deviceType }) => {
  const { t } = useTranslator()
  const Items = useMemo(() => getItems(t('project.section_steps.steps') as unknown as TypeSectionSteps['steps']), [t])
  const { isMobile } = useDeviceType()
  const [displayAsSlider, setDisplayAsSlider] = useState(deviceType === 'mobile')

  const headingId = useMemo(() => t('project.internal_page_links')?.[3]?.id?.substring(1) as string, [t])

  useEffect(() => {
    setDisplayAsSlider(isMobile)
  }, [isMobile])

  return (
    <Section aria-labelledby={headingId}>
      <hgroup>
        <div>
          <h2 id={headingId}>{t('project.section_steps.title')}</h2>
          <div dangerouslySetInnerHTML={{ __html: t('project.section_steps.description') as string }}></div>
        </div>
      </hgroup>
      {displayAsSlider ?
        <SliderFlickity>
          {Items}
        </SliderFlickity>
        :
        <ul>
          {Items}
        </ul>
      }
      <div className='s--footer' dangerouslySetInnerHTML={{ __html: t('project.section_steps.section_footer') as string }}></div>
    </Section>
  )
})

const Section = styled.section`
  ${designGrid({})}
  margin-bottom: 75px;
  overflow-x: hidden;
  padding-top: 75px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 110px;
    overflow-x: unset;
    padding-top: 110px;
  }

  > hgroup {
    grid-column: 1 / span 6;
    margin-bottom: 50px;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 5;
      margin-bottom: 0;
    }

    > div {
      display: flex;
      flex-direction: column;

      ${mq.greaterThan('tablet')} {
        position: sticky;
        top: 100px;
      }

      > div {
        display: flex;
        flex-direction: column;
        gap: 8px;
      }
    }

    p {
      ${presets.font26()}
      font-weight: 400 !important;
    }
  }

  h2 {
    ${presets.font40_60()}
    margin-bottom: 15px;
    text-align: left;
  }

  .s--footer {
    align-items: flex-start;
    display: flex;
    grid-column: 1 / span 6;
    justify-content: center;
    margin-top: 50px;

    ${mq.greaterThan('tablet')} {
      grid-column: 4 / span 7;
    }

    p {
      text-align: center;
    }
  }

  ul {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 100px;
      grid-column: 6 / span 7;
      justify-content: flex-start;
    }
  }

  li {
    margin-right: 30px;
    width: 250px;

    ${mq.greaterThan('tablet')} {
      margin-right: 0;
      width: 100%;
    }
  }

  article {
    display: flex;
    flex-direction: column;
    gap: 10px;
    position: relative;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      flex-direction: row;
      gap: 15px;
    }

    ${mq.greaterThan('desktop-md')} {
      gap: 30px;
    }

    ${mq.greaterThan('desktop')} {
      gap: 50px;
    }

    hgroup {
      width: 250px;

      ${mq.greaterThan('tablet')} {
        width: 100%;
      }
    }
  }

  .slider-wrapper {
    min-height: 320px;
  }

  .media-wrapper {
    align-self: stretch;
    background-color: ${rgbAlpha('--color-white', .1)};
    border-radius: 20px;
    flex-shrink: 0;
    height: 200px;
    max-width: 250px;
    overflow: hidden;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      max-width: 290px;
    }

    ${mq.greaterThan('desktop-md')} {
      max-width: 370px;
    }
  }

  .media {
    height: 100%;
    object-fit: cover;
    width: 100%;
  }

  h3, p, span {
    text-align: center;

    ${mq.greaterThan('tablet')} {
      text-align: left;
    }
  }

  h3 {
    align-items: center;
    display: flex;
    flex-direction: column;
    ${presets.font20_40()}
    gap: 10px;
    justify-content: flex-start;
    margin-bottom: 2px;

    ${mq.greaterThan('tablet')} {
      align-items: flex-start;
      margin-bottom: 10px;
    }

    span {
      word-break: break-all;

      &:first-child {
        color: var(--color-gold);
        display: block;
        ${presets.font30_step_number()}
        left: 10px;
        position: absolute;
        top: 10px;

        ${mq.greaterThan('tablet')} {
          left: auto;
          opacity: .4;
          position: static;
          top: auto;
          width: 100%;
        }
      }
    }
  }

  p {
    ${presets.font14_15()}
    margin-bottom: 2px;
  }
`
